import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { useState, type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import { useTranslation, Trans } from "react-i18next";

import {
  Arrange,
  Button,
  FloatingLabelInput,
  Link,
  Message,
  Spacer,
  Tooltip,
  Typography,
} from "@aviary";
import { sharedRoutes } from "@shared/data/sharedRoutes";
import {
  AnalyticsEvent,
  useAnalyticsLogger,
  useBreakpoints,
  useUnauthUrlFields,
} from "@shared/hooks";
import { FontAwesomeIcon } from "@shared/react-fontawesome";
import { useSearchParams } from "@shared/react-router-dom";
import {
  FirstNameInput,
  FormWrapper,
  LastNameInput,
  IsNameValid,
} from "@unauthenticated/shared/components";
import { DispensaryNameInput } from "@unauthenticated/shared/components/DispensaryNameInput";
import { TermsOfServiceCheckbox } from "@unauthenticated/shared/components/TermsOfServiceCheckbox";
import { l } from "@unauthenticated/wellevateAuthorize/practitioners/locales/i18n";
import {
  type WellevateSignupFormData,
  AccountCreationStep,
} from "@unauthenticated/wellevateAuthorize/practitioners/types";

import * as styles from "./WellevateAccountDetailsPage.styles";

interface Props {
  formData: WellevateSignupFormData;
  onSetFormData: Dispatch<SetStateAction<WellevateSignupFormData>>;
}

const WellevateAccountDetailsPage = ({ formData, onSetFormData }: Props) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { tablet } = useBreakpoints();
  const [practitionerFirstName] = useState<string>(formData.firstName);
  const logAnalyticsEvent = useAnalyticsLogger();
  const unauthUrlFields = useUnauthUrlFields();
  const isMobile = tablet.lessThan;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;

    onSetFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const updateTimestamp = tosTimestamp => {
    onSetFormData(prevState => ({ ...prevState, tosTimestamp }));
  };

  const isFormValid = () =>
    IsNameValid(formData.firstName) &&
    IsNameValid(formData.lastName) &&
    !!formData.practitionerType &&
    !!formData.tosTimestamp;

  const getDefaultDispensaryName = () => {
    const { firstName, lastName } = formData;

    if (!firstName || !lastName) return "";

    const lastChar = lastName[lastName.length - 1];
    const s = lastChar === "s" || lastChar === "S" ? "" : "s";
    const fullName = `${firstName} ${lastName}'${s}`;

    return t(l.common.DispensaryNameDefault, { fullName });
  };

  const handleClick = () => {
    if (formData.dispensaryName === "") {
      onSetFormData({
        ...formData,
        dispensaryName: getDefaultDispensaryName(),
      });
    }

    setSearchParams({ step: AccountCreationStep.ACCOUNT_CREATION });

    logAnalyticsEvent({
      eventName: AnalyticsEvent.WELLEVATE_MIGRATION_TO_ACCOUNT_CREATION,
      payload: {
        ...unauthUrlFields,
        source_account_id: formData.wellevatePractitionerId,
      },
    });
  };

  const renderButton = () => {
    const isButtonDisabled = !isFormValid();

    return (
      <Tooltip
        css={styles.buttonTooltip}
        isTooltipHidden={!isButtonDisabled}
        tooltipContent={t(l.practitionerSignUp.disabledNextButtonTooltip)}
      >
        <Button disabled={isButtonDisabled} isFullWidth onClick={handleClick}>
          {t(l.common.AccountDetails.buttonLabel)}
        </Button>
      </Tooltip>
    );
  };

  const renderModalityError = () => (
    <Message titleContent={t(l.common.AccountDetails.modalityError.title)} isColor="danger">
      <Arrange justify="spaceBetween" alignment="center" isWrapped={isMobile}>
        <Trans i18nKey={l.common.AccountDetails.modalityError.subTitle}>
          We've imported your modality from Wellevate, but could not match it to one in our
          database. Please contact help & support
          <Link href={sharedRoutes.external.wellevateMigrationSupport} noExternalIcon>
            help & support
          </Link>
          for next steps.
        </Trans>
        <Button
          href={sharedRoutes.external.wellevateMigrationSupport}
          target="_blank"
          rel="noreferrer"
          isColor="system"
          css={styles.helpButton}
        >
          {t(l.common.AccountDetails.modalityError.buttonLabel)}
        </Button>
      </Arrange>
    </Message>
  );

  return (
    <Arrange justify="center" isFullwidth spacing="none">
      <Message isColor="success" titleContent={t(l.common.AccountDetails.message.title)}>
        <Typography>
          {t(l.common.AccountDetails.message.content, {
            practitionerName: practitionerFirstName,
          })}
        </Typography>
      </Message>
      <Typography type="h2" css={styles.title}>
        {t(l.common.AccountDetails.title)}
      </Typography>
      <Typography css={styles.subTitle}>{t(l.common.AccountDetails.subTitle)}</Typography>
      <FormWrapper>
        <form noValidate>
          <div css={styles.row}>
            <div css={styles.name}>
              <FirstNameInput
                value={formData.firstName}
                onChange={handleChange}
                required
                isLoading={false}
                isDirty
              />
              <Spacer width="oneHalf" />
              <LastNameInput
                value={formData.lastName}
                onChange={handleChange}
                required
                isLoading={false}
                isDirty
              />
            </div>

            <Tooltip
              tooltipContent={t(l.practitionerSignUp.PracNameTooltip)}
              data-testid="prac-name-tooltip"
              css={styles.tooltip}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </div>
          <div css={styles.row}>
            <DispensaryNameInput
              value={formData.dispensaryName}
              onChange={handleChange}
              placeholder={getDefaultDispensaryName()}
              required
            />
            <Tooltip
              tooltipContent={t(l.practitionerSignUp.DispensaryNameTooltip)}
              css={styles.tooltip}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </div>
          <div css={styles.row}>
            <FloatingLabelInput
              label={t(l.common.modality)}
              type="text"
              id={t(l.common.modality)}
              value={formData.practitionerType}
              disabled
            />
            <Tooltip
              tooltipContent={t(l.common.AccountDetails.modalityTooltip)}
              css={styles.tooltip}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </div>
          {!formData.practitionerType.length && renderModalityError()}
          <TermsOfServiceCheckbox
            updateTimestamp={updateTimestamp}
            isChecked={!!formData.tosTimestamp}
          />
          <Spacer height="oneHalf" />
          {renderButton()}
        </form>
      </FormWrapper>
    </Arrange>
  );
};

export { WellevateAccountDetailsPage };
